import { supabase } from '@/lib/supabase'
import type { Recording } from '@/types/database'

export const RECORDINGS_BUCKET = 'recordings'

/**
 * File extension for a MediaRecorder mime type. The value can carry codec
 * params ("video/webm;codecs=vp8,opus"), so match on the container prefix.
 */
export function extensionForMimeType(mimeType: string): string {
  const container = mimeType.split(';')[0].trim().toLowerCase()
  switch (container) {
    case 'video/webm':
      return 'webm'
    case 'video/mp4':
      return 'mp4'
    case 'video/quicktime':
      return 'mov'
    default:
      return 'bin'
  }
}

export interface UploadRecordingParams {
  blob: Blob
  clientId: string
  storyId: string
  userId: string | null
  mimeType: string
  durationSeconds: number | null
}

/**
 * Upload a recording to the private `recordings` bucket and insert its row.
 * Storage RLS requires the first path segment to be the caller's client_id.
 */
export async function uploadRecording({
  blob,
  clientId,
  storyId,
  userId,
  mimeType,
  durationSeconds,
}: UploadRecordingParams): Promise<Recording> {
  const ext = extensionForMimeType(mimeType)
  const storagePath = `${clientId}/${storyId}/${crypto.randomUUID()}.${ext}`

  const { error: uploadError } = await supabase.storage
    .from(RECORDINGS_BUCKET)
    .upload(storagePath, blob, { contentType: mimeType, upsert: false })

  if (uploadError) throw uploadError

  const { data, error } = await supabase
    .from('recordings')
    .insert({
      client_id: clientId,
      story_id: storyId,
      user_id: userId,
      storage_path: storagePath,
      mime_type: mimeType,
      size_bytes: blob.size,
      duration_seconds: durationSeconds,
    })
    .select()
    .single()

  if (error) {
    // The object is already up but unreferenced — remove it so a retry doesn't
    // leave the bucket accumulating orphans.
    await supabase.storage.from(RECORDINGS_BUCKET).remove([storagePath])
    throw error
  }

  return data as unknown as Recording
}

/** Signed URL for playback of a private recording object. */
export async function createRecordingSignedUrl(storagePath: string, expiresInSeconds = 3600) {
  const { data, error } = await supabase.storage
    .from(RECORDINGS_BUCKET)
    .createSignedUrl(storagePath, expiresInSeconds)
  if (error) throw error
  return data.signedUrl
}
